<div class="min-h-screen mx-auto px-4 py-6 md:p-8 bg-gray-50">
  <!-- Header -->
  <div class="mb-6 md:mb-8">
    <h1 class="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
      {{ "DASHBOARD.ACTIVE_QUOTES.TITLE" | translate }}
    </h1>
    <p class="text-sm md:text-base text-gray-600">
      {{ "DASHBOARD.ACTIVE_QUOTES.DESCRIPTION" | translate }}
    </p>
  </div>

  <!-- Search and Filter Controls -->
  <div class="bg-white rounded-xl border border-gray-200 p-4 mb-6">
    <div class="flex flex-col md:flex-row gap-4 items-center justify-between">
      <!-- Search -->
      <div class="relative flex-1 w-full md:max-w-md">
        <svg
          class="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          ></path>
        </svg>
        <input
          type="text"
          [(ngModel)]="searchTerm"
          (input)="onSearchChange()"
          [placeholder]="
            'DASHBOARD.ACTIVE_QUOTES.SEARCH_PLACEHOLDER' | translate
          "
          class="w-full pl-10 pr-12 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm md:text-base"
        />
        <svg
          class="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          ></path>
        </svg>
        <button
          *ngIf="hasSearchCriteria()"
          (click)="clearSearch()"
          class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
          [title]="'DASHBOARD.ACTIVE_QUOTES.CLEAR_SEARCH' | translate"
        >
          <svg
            class="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            ></path>
          </svg>
        </button>
      </div>

      <!-- Filter and Actions -->
      <div class="flex flex-wrap gap-2 md:gap-3 w-full md:w-auto">
        <!-- Create New Quote Button -->
        <button
          (click)="createNewQuote()"
          class="w-full md:w-auto bg-teal-500 hover:bg-teal-600 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors text-xs md:text-sm"
        >
          <svg
            class="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 4v16m8-8H4"
            ></path>
          </svg>
          {{ "DASHBOARD.ACTIVE_QUOTES.CREATE_NEW_QUOTE" | translate }}
        </button>
      </div>
    </div>
  </div>

  <!-- Quote Count -->
  <div
    class="mb-4 text-xs md:text-sm text-gray-600"
    *ngIf="!isLoading && !error"
  >
    {{
      "DASHBOARD.ACTIVE_QUOTES.SHOWING_QUOTES"
        | translate : { count: quotes.length, total: totalElements }
    }}
  </div>

  <!-- Loading State -->
  <app-loader
    [show]="isLoading"
    [mainMessage]="'DASHBOARD.ACTIVE_QUOTES.LOADING' | translate"
    [subMessage]="'DASHBOARD.ACTIVE_QUOTES.FETCHING_DATA' | translate"
  >
  </app-loader>

  <!-- Error State -->
  <div
    *ngIf="error && !isLoading"
    class="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg mb-6"
  >
    <div class="flex">
      <div class="flex-shrink-0">
        <svg
          class="h-5 w-5 text-red-500"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
            clip-rule="evenodd"
          />
        </svg>
      </div>
      <div class="ml-3">
        <h3 class="text-sm font-medium text-red-800">{{ error }}</h3>
        <div class="mt-4">
          <button
            (click)="loadActiveQuotes()"
            class="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-500"
          >
            {{ "COMMON.RETRY" | translate }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Empty State -->
  <div
    *ngIf="!isLoading && !error && quotes.length === 0"
    class="bg-white rounded-xl border border-gray-200 p-6 md:p-8 text-center"
  >
    <div
      class="mx-auto flex items-center justify-center h-12 w-12 md:h-16 md:w-16 rounded-full bg-blue-50 mb-4"
    >
      <svg
        class="h-6 w-6 md:h-8 md:w-8 text-blue-600"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="1.5"
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    </div>
    <h3 class="text-lg md:text-xl font-semibold text-gray-900 mb-2">
      {{ "DASHBOARD.ACTIVE_QUOTES.NO_QUOTES_TITLE" | translate }}
    </h3>
    <p class="text-sm md:text-base text-gray-600 mb-6">
      {{ "DASHBOARD.ACTIVE_QUOTES.NO_QUOTES_DESC" | translate }}
    </p>
  </div>

  <!-- Quotes List -->
  <div *ngIf="!isLoading && !error && quotes.length > 0" class="space-y-4">
    <div
      *ngFor="let quote of quotes"
      class="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-300"
    >
      <div class="p-4 md:p-6">
        <div
          class="flex flex-col md:flex-row items-start justify-between gap-4 mb-4"
        >
          <!-- Left Section -->
          <div class="flex-1 flex flex-col gap-4 md:gap-4 w-full">
            <!-- Quote ID and Status -->
            <div class="flex items-start gap-3 items-center">
              <div class="flex-shrink-0">
                <div
              class="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold"
              [ngClass]="
                getProviderColor(quote.providerName || '')
              "
            >
              {{
                getProviderInitials(quote.providerName || "")
              }}
            </div>
              </div>
              <div class="flex-1 min-w-0 gap-2">
                <p class="text-sm font-semibold text-gray-900 truncate">
                  {{
                    quote.providerName ||
                      quote.quoteRequest?.quoteRequestReferenceNo
                  }}
                </p>
                <div class="flex items-center gap-2">
                  <span
                    class="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium"
                    [ngClass]="{
                      'bg-green-100 text-green-800':
                        quote.status === 'COMPLETED',
                      'bg-yellow-100 text-yellow-800':
                        quote.status === 'PENDING',
                      'bg-red-100 text-red-800': quote.status === 'FAILED'
                    }"
                  >
                    {{
                      quote.status === "COMPLETED"
                        ? ("DASHBOARD.ACTIVE_QUOTES.ACTIVE" | translate)
                        : quote.status
                    }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Information Row -->
            <div
              class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6"
            >
              <!-- Company -->
              <div class="flex gap-2 items-center">
                <svg
                  width="35"
                  height="35"
                  viewBox="0 0 28 28"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect width="28" height="28" rx="8.75" fill="#EFF6FF" />
                  <path
                    d="M10.5 19.8327V9.33268C10.5 9.02326 10.6229 8.72652 10.8417 8.50772C11.0605 8.28893 11.3572 8.16602 11.6667 8.16602H16.3333C16.6428 8.16602 16.9395 8.28893 17.1583 8.50772C17.3771 8.72652 17.5 9.02326 17.5 9.33268V19.8327H10.5Z"
                    stroke="#155DFC"
                    stroke-width="1.16667"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M10.5003 14H9.33366C9.02424 14 8.72749 14.1229 8.5087 14.3417C8.28991 14.5605 8.16699 14.8572 8.16699 15.1667V18.6667C8.16699 18.9761 8.28991 19.2728 8.5087 19.4916C8.72749 19.7104 9.02424 19.8333 9.33366 19.8333H10.5003"
                    stroke="#155DFC"
                    stroke-width="1.16667"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M17.5 12.25H18.6667C18.9761 12.25 19.2728 12.3729 19.4916 12.5917C19.7104 12.8105 19.8333 13.1072 19.8333 13.4167V18.6667C19.8333 18.9761 19.7104 19.2728 19.4916 19.4916C19.2728 19.7104 18.9761 19.8333 18.6667 19.8333H17.5"
                    stroke="#155DFC"
                    stroke-width="1.16667"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M12.833 10.5H15.1663"
                    stroke="#155DFC"
                    stroke-width="1.16667"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M12.833 12.834H15.1663"
                    stroke="#155DFC"
                    stroke-width="1.16667"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M12.833 15.166H15.1663"
                    stroke="#155DFC"
                    stroke-width="1.16667"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M12.833 17.5H15.1663"
                    stroke="#155DFC"
                    stroke-width="1.16667"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <div class="flex flex-col gap-1">
                  <p class="text-xs text-gray-500">
                    {{ "DASHBOARD.ACTIVE_QUOTES.COMPANY" | translate }}
                  </p>
                  <p class="text-sm font-semibold text-gray-900">
                    {{ quote.companyName || quote.quoteRequest?.companyName }}
                  </p>
                </div>
              </div>

              <!-- Employees -->
              <div class="flex gap-2 items-center">
                <svg
                  width="35"
                  height="35"
                  viewBox="0 0 28 28"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect width="28" height="28" rx="8.75" fill="#FAF5FF" />
                  <path
                    d="M16.3337 19.25V18.0833C16.3337 17.4645 16.0878 16.871 15.6502 16.4334C15.2127 15.9958 14.6192 15.75 14.0003 15.75H10.5003C9.88149 15.75 9.28799 15.9958 8.85041 16.4334C8.41282 16.871 8.16699 17.4645 8.16699 18.0833V19.25"
                    stroke="#9810FA"
                    stroke-width="1.16667"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M16.333 8.82422C16.8334 8.95393 17.2765 9.24612 17.5928 9.65492C17.9092 10.0637 18.0808 10.566 18.0808 11.0829C18.0808 11.5998 17.9092 12.102 17.5928 12.5108C17.2765 12.9196 16.8334 13.2118 16.333 13.3416"
                    stroke="#9810FA"
                    stroke-width="1.16667"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M19.833 19.2503V18.0837C19.8326 17.5667 19.6605 17.0645 19.3438 16.6559C19.0271 16.2473 18.5836 15.9554 18.083 15.8262"
                    stroke="#9810FA"
                    stroke-width="1.16667"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M12.2503 13.4167C13.539 13.4167 14.5837 12.372 14.5837 11.0833C14.5837 9.79467 13.539 8.75 12.2503 8.75C10.9617 8.75 9.91699 9.79467 9.91699 11.0833C9.91699 12.372 10.9617 13.4167 12.2503 13.4167Z"
                    stroke="#9810FA"
                    stroke-width="1.16667"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <div class="flex flex-col gap-1">
                  <p class="text-xs text-gray-500">
                    {{ "DASHBOARD.ACTIVE_QUOTES.EMPLOYEES" | translate }}
                  </p>
                  <p class="text-sm font-semibold text-gray-900">
                    {{
                      quote.companyNumberOfEmployee ||
                        (quote.quoteRequest?.primaryCount || 0) +
                          (quote.quoteRequest?.dependentCount || 0)
                    }}
                  </p>
                </div>
              </div>

              <!-- Expires -->
              <div class="flex gap-2 items-center">
                <svg
                  width="35"
                  height="35"
                  viewBox="0 0 28 28"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect width="28" height="28" rx="8.75" fill="#F0FDF4" />
                  <path
                    d="M11.667 8.16602V10.4993"
                    stroke="#00A63E"
                    stroke-width="1.16667"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M16.333 8.16602V10.4993"
                    stroke="#00A63E"
                    stroke-width="1.16667"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M18.0833 9.33398H9.91667C9.27233 9.33398 8.75 9.85632 8.75 10.5007V18.6673C8.75 19.3116 9.27233 19.834 9.91667 19.834H18.0833C18.7277 19.834 19.25 19.3116 19.25 18.6673V10.5007C19.25 9.85632 18.7277 9.33398 18.0833 9.33398Z"
                    stroke="#00A63E"
                    stroke-width="1.16667"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M8.75 12.834H19.25"
                    stroke="#00A63E"
                    stroke-width="1.16667"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <div class="flex flex-col gap-1">
                  <p class="text-xs text-gray-500">
                    {{ "DASHBOARD.ACTIVE_QUOTES.EXPIRES" | translate }}
                  </p>
                  <p class="text-sm font-semibold text-gray-900">
                    {{ getExpiryDate(quote) }}
                  </p>
                </div>
              </div>

              <!-- Location -->
              <div class="flex gap-2 items-center">
                <svg
                  width="35"
                  height="35"
                  viewBox="0 0 28 28"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect width="28" height="28" rx="8.75" fill="#FFF7ED" />
                  <path
                    d="M15.7497 8.16602H10.4997C10.1903 8.16602 9.89351 8.28893 9.67472 8.50772C9.45592 8.72652 9.33301 9.02326 9.33301 9.33268V18.666C9.33301 18.9754 9.45592 19.2722 9.67472 19.491C9.89351 19.7098 10.1903 19.8327 10.4997 19.8327H17.4997C17.8091 19.8327 18.1058 19.7098 18.3246 19.491C18.5434 19.2722 18.6663 18.9754 18.6663 18.666V11.0827L15.7497 8.16602Z"
                    stroke="#F54900"
                    stroke-width="1.16667"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M15.167 8.16602V10.4993C15.167 10.8088 15.2899 11.1055 15.5087 11.3243C15.7275 11.5431 16.0242 11.666 16.3337 11.666H18.667"
                    stroke="#F54900"
                    stroke-width="1.16667"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M12.8337 12.25H11.667"
                    stroke="#F54900"
                    stroke-width="1.16667"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M16.3337 14.584H11.667"
                    stroke="#F54900"
                    stroke-width="1.16667"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M16.3337 16.916H11.667"
                    stroke="#F54900"
                    stroke-width="1.16667"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <div class="flex flex-col gap-1">
                  <p class="text-xs text-gray-500">
                    {{ "DASHBOARD.ACTIVE_QUOTES.LOCATION" | translate }}
                  </p>
                  <p class="text-sm font-semibold text-gray-900">Medina</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Right Section - Quote Value -->
          <div class="text-left md:text-right w-full md:w-auto md:ml-6">
            <p class="text-xl md:text-2xl font-bold text-gray-900">
              {{
                formatAmount(
                  quote.policyTaxablePremium || quote.quoteRequest?.amount || 0
                )
              }}
            </p>
            <p class="text-xs text-gray-500 mt-1">
              {{ "DASHBOARD.ACTIVE_QUOTES.PRICE_BEFORE_VAT" | translate }}
            </p>
          </div>
        </div>
        <p class="text-xs text-gray-500 p-2 mt-2 bg-gray-100 rounded-lg">
          {{ "DASHBOARD.ACTIVE_QUOTES.QUOTED_BY" | translate }}
          {{ quote.entityNameAr || quote.quoteRequest?.entityNameAr }}
          {{ "DASHBOARD.ACTIVE_QUOTES.ON" | translate }}
          {{
            formatRequestDate(quote.requestDate) ||
              formatDate(quote.quoteRequest?.requestDate)
          }}
        </p>
        <!-- Action Buttons -->
        <div
          class="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-2 md:gap-3"
        >
          <button
            (click)="viewQuote(quote)"
            class="bg-teal-500 hover:bg-teal-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm"
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              ></path>
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              ></path>
            </svg>
            {{ "DASHBOARD.ACTIVE_QUOTES.VIEW_QUOTE" | translate }}
          </button>
          <button
            *ngIf="quote.status === 'COMPLETED'"
            (click)="convertToPolicy(quote)"
            class="w-full sm:w-auto sm:ml-auto bg-teal-500 hover:bg-teal-600 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors text-xs md:text-sm"
          >
            {{ "DASHBOARD.ACTIVE_QUOTES.CONVERT_TO_POLICY" | translate }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Pagination Controls -->
  <div
    *ngIf="!isLoading && !error && quotes.length > 0 && totalPages > 1"
    class="mt-6 md:mt-8 flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 rounded-lg"
  >
    <!-- Pagination Info -->
    <div class="flex flex-1 justify-between sm:hidden">
      <button
        [disabled]="isFirstPage"
        (click)="goToPreviousPage()"
        class="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {{ "COMMON.PREVIOUS" | translate }}
      </button>
      <button
        [disabled]="isLastPage"
        (click)="goToNextPage()"
        class="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {{ "COMMON.NEXT" | translate }}
      </button>
    </div>

    <div class="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
      <div>
        <p class="text-sm text-gray-700">
          {{ "COMMON.SHOWING" | translate }}
          <span class="font-medium">{{ currentPage * pageSize + 1 }}</span>
          {{ "COMMON.TO" | translate }}
          <span class="font-medium">{{
            Math.min((currentPage + 1) * pageSize, totalElements)
          }}</span>
          {{ "COMMON.OF" | translate }}
          <span class="font-medium">{{ totalElements }}</span>
          {{ "COMMON.RESULTS" | translate }}
        </p>
      </div>
      <div>
        <nav
          class="isolate inline-flex -space-x-px rounded-md shadow-sm"
          aria-label="Pagination"
        >
          <!-- Previous Page Button -->
          <button
            [disabled]="isFirstPage"
            (click)="goToPreviousPage()"
            class="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span class="sr-only">{{ "COMMON.PREVIOUS" | translate }}</span>
            <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fill-rule="evenodd"
                d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                clip-rule="evenodd"
              />
            </svg>
          </button>

          <!-- Page Numbers -->
          <ng-container *ngFor="let pageNum of pageNumbers">
            <button
              *ngIf="pageNum !== -1"
              [class]="
                currentPage === pageNum - 1
                  ? 'relative z-10 inline-flex items-center bg-teal-600 px-4 py-2 text-sm font-semibold text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600'
                  : 'relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
              "
              (click)="goToPage(pageNum - 1)"
            >
              {{ pageNum }}
            </button>
            <span
              *ngIf="pageNum === -1"
              class="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 focus:outline-offset-0"
            >
              ...
            </span>
          </ng-container>

          <!-- Next Page Button -->
          <button
            [disabled]="isLastPage"
            (click)="goToNextPage()"
            class="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span class="sr-only">{{ "COMMON.NEXT" | translate }}</span>
            <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fill-rule="evenodd"
                d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
        </nav>
      </div>
    </div>
  </div>
</div>
